import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';

import './index.css';

import { BrowserRouter, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertProvider, useAlert, useErrorAlert } from './alert/AlertContext';
import { toast } from 'react-toastify';
import { notifyPermissionsChanged } from './permission/PermissionUtil';

axios.defaults.baseURL = '/app/web';

let navigate;

export const setNavigator = (navigator) => {
    navigate = navigator;
};

let alert;
let errorAlert;
let toastInfoHandler;
let toastSuccessHandler;

export const setAlerter = (alerter) => {
    alert = alerter;
};

export const setErrorAlerter = (errorAlerter) => {
    errorAlert = errorAlerter;
};

export const setToastInfoHandler = (toaster) => {
    toastInfoHandler = toaster
}

export const setToastSuccessHandler = (toaster) => {
    toastSuccessHandler = toaster
}

axios.interceptors.response.use(
    (response) => {
        if (response?.data?.message) {
            toastSuccessHandler?.(response.data.message);
        }
        return response
    },
    (error) => {
        if (error.response && error.response.status === 400) {
            errorAlert?.(error?.response?.data?.title || 'Error', error?.response?.data?.message || 'Error', error?.response?.data?.detail);
        } else if (error.response && error.response.status === 401) {
            if (localStorage.getItem('authToken') !== null) {
                localStorage.removeItem('authToken');
                localStorage.removeItem('displayName');
                localStorage.removeItem('permissions');
                localStorage.removeItem('administrator');
                localStorage.removeItem('user');
                notifyPermissionsChanged();
                if (location.pathname !== '/login') {
                    const redirectTo = location.pathname + location.search;
                    const urlToGoTo = `/login?redirect=${encodeURIComponent(redirectTo)}`;
                    if (navigate) {
                        navigate(urlToGoTo);
                    }
                }
            }
        } else if (error.response && error.response.status === 403) {
            setTimeout(() => {
                errorAlert?.(error?.response?.data?.title || 'No Permission', error?.response?.data?.message || 'You do not have permission', error?.response?.data?.detail);
            }, 100);
        } else if (error.response && error.response.status === 404) {
            errorAlert?.(error?.response?.data?.title || 'Error', error?.response?.data?.message || 'Not Found', error?.response?.data?.detail);
        } else if (error.response && error.response.status === 500) {
            errorAlert?.(error?.response?.data?.title || 'Error', error?.response?.data?.message || 'Error', error?.response?.data?.detail);
        } else if (error.response && error.response.status === 504) {
            errorAlert?.('Error', 'A request has timed out');
        } else if (error.response?.status === 428) {
            // Do nothing as will likely already be handled
        } else {
            errorAlert?.(error?.response?.data?.title || 'Error', error?.response?.data?.message || 'Error', error?.response?.data?.detail);
        }
        return Promise.reject(error);
    }
);

const Root = () => {
    const navigator = useNavigate();
    const alerter = useAlert();
    const errorAlerter = useErrorAlert();

    const showInfoToast = (message) => {
        toast.info(message, {
            position: 'top-right',
        });
    };

    const showSuccessToast = (message) => {
        toast.success(message);
    };

    React.useEffect(() => {
        setNavigator(navigator);
        setAlerter(alerter);
        setErrorAlerter(errorAlerter);
        setToastInfoHandler(showInfoToast);
        setToastSuccessHandler(showSuccessToast);
    }, [navigator]);

    return <App />;
};

const root = createRoot(document.getElementById('root'));
root.render(
    <BrowserRouter>
        <AlertProvider>
            <Root />
        </AlertProvider>
    </BrowserRouter>
);
